<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewMdas' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to MDAs">
        MDAs
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View MDA</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <div class="crud-form position-relative">

          <div class="form-inner mb-2">
            <div class="form-header d-flex justify-content-between align-items-center">
              <h1 class="mb-0">MDA</h1>
            </div>

            <div class="mda-content-area position-relative">
              <div v-if="!isDataLoaded" class="loading-overlay text-center">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading MDA details...</p>
              </div>

              <div v-if="isDataLoaded" class="form-body mt-3 mb-3">

                <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show mx-3 mb-3"
                  role="alert">
                  {{ errorMessage }}
                  <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Name</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control form-view-only-input" v-model="form.name" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Organisation Code</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control form-view-only-input" v-model="form.orgCode" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Sub-sector</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control form-view-only-input" :value="subSectorLabel" readonly />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-row-label">
                    <label class="form-label">Group</label>
                  </div>
                  <div class="form-row-input">
                    <input type="text" class="form-control form-view-only-input" :value="groupTypeLabel" readonly />
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { useRoute } from 'vue-router'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const id = route.params.id

const isDataLoaded = ref(false)
const errorMessage = ref('')
const subsectors = ref([])
const groupTypes = ref([])

const form = reactive({
  name: '',
  orgCode: '',
  subSectorId: '',
  groupTypeId: '',
})

const subSectorLabel = computed(() => {
  const found = subsectors.value.find(s => s.id === form.subSectorId)
  return found ? found.description : form.subSectorId || 'N/A'
})

const groupTypeLabel = computed(() => {
  const found = groupTypes.value.find(g => g.id === form.groupTypeId)
  return found ? found.description : form.groupTypeId || 'N/A'
})

const fetchMda = async () => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    form.name = data.name || ''
    form.orgCode = data.orgCode || ''
    form.subSectorId = data.subSectorId || ''
    form.groupTypeId = data.groupTypeId || ''
  } catch (error) {
    console.error('Failed to fetch MDA:', error)
    errorMessage.value = 'Failed to load MDA details. Please try again.'
  } finally {
    isDataLoaded.value = true
  }
}

const fetchSubSectors = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/sub-sector`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    subsectors.value = response.data.map(s => ({
      id: s.id,
      description: s.description,
    })).sort((a, b) => a.description.localeCompare(b.description))
  } catch (error) {
    console.error('Failed to fetch sub-sectors:', error)
  }
}

const fetchGroupTypes = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/group-type`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    groupTypes.value = response.data.map(g => ({
      id: g.id,
      description: g.name,
    })).sort((a, b) => a.description.localeCompare(b.description))
  } catch (error) {
    console.error('Failed to fetch group types:', error)
  }
}

onMounted(async () => {
  await Promise.all([fetchSubSectors(), fetchGroupTypes()])
  await fetchMda()
  document.title = 'View MDA'
})
</script>

<style scoped>
.mda-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.form-outer {
  width: 100%;
  max-width: 800px;
}

.crud-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 30%;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
}

.form-row-input {
  flex: 1;
}

@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
  }

  .form-row-label {
    flex: 0 0 40%;
  }
}

@media screen and (max-width: 768px) {
  .form-header h1 {
    font-size: 1.25rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }
}
</style>
